import { ReactNode } from 'react'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import GmailBadge from './GmailBadge'
import { LogOut, Settings, ListFilter, Activity, Users, Tags, ShieldAlert, CreditCard } from 'lucide-react'
import filterhawkLogo from '@/assets/filterhawk.svg'
import filterhawkLogoWhite from '@/assets/filterhawk-white.svg'

interface DashboardLayoutProps {
  children: ReactNode
  currentUser?: {
    name: string
    email: string
    avatarUrl?: string
    admin?: boolean
  }
  gmailConnection: {
    id: number
    email: string
    syncStatus: string
    active: boolean
  } | null
  account?: {
    id: number
    name: string
  } | null
  activeTab?: 'workflows' | 'activity' | 'senders'
}

export default function DashboardLayout({
  children,
  currentUser,
  gmailConnection,
  account,
  activeTab = 'activity',
}: DashboardLayoutProps) {
  const handleSignOut = () => {
    router.delete('/logout')
  }

  const initials = currentUser?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase() || 'U'

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Top Navigation Bar */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 py-4 sm:py-0 sm:h-16">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <img
                src={filterhawkLogo}
                alt="FilterHawk"
                className="h-8 w-auto dark:hidden"
              />
              <img
                src={filterhawkLogoWhite}
                alt="FilterHawk"
                className="h-8 w-auto hidden dark:block"
              />
            </div>

            {/* Right side: Gmail badge + User menu */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <GmailBadge gmailConnection={gmailConnection} />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                    <Avatar className="bg-gray-100 dark:bg-gray-700">
                      <AvatarImage src={currentUser?.avatarUrl} alt={currentUser?.name} />
                      <AvatarFallback className="bg-gray-100 dark:bg-gray-700">{initials}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="flex flex-col space-y-1 p-2">
                    <p className="text-sm font-medium">{currentUser?.name}</p>
                    <p className="text-xs text-muted-foreground">{currentUser?.email}</p>
                  </div>
                  <DropdownMenuItem
                    onClick={() => router.get('/user_settings/edit')}
                  >
                    <Settings className="mr-2 h-4 w-4" />
                    <span>User Settings</span>
                  </DropdownMenuItem>
                  {account && (
                    <>
                      <DropdownMenuItem
                        onClick={() => router.get(`/accounts/${account.id}/settings/edit`)}
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        <span>Account Settings</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => router.get(`/accounts/${account.id}/gmail_labels`)}
                      >
                        <Tags className="mr-2 h-4 w-4" />
                        <span>Gmail Labels</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => window.location.href = '/subscription'}
                      >
                        <CreditCard className="mr-2 h-4 w-4" />
                        <span>Manage Subscription</span>
                      </DropdownMenuItem>
                    </>
                  )}
                  {currentUser?.admin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => router.get('/admin')}
                        className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
                      >
                        <ShieldAlert className="mr-2 h-4 w-4" />
                        <span>Admin</span>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sign out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-col min-[400px]:flex-row min-[400px]:space-x-8 -mb-px">
            <button
              onClick={() => account && router.get(`/accounts/${account.id}/activity`)}
              disabled={!account}
              className={`py-2 min-[400px]:py-4 px-1 border-b-2 min-[400px]:border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'activity'
                  ? 'border-transparent min-[400px]:border-blue-600 min-[400px]:dark:border-blue-400 text-blue-600 dark:text-blue-400 cursor-pointer'
                  : account
                    ? 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 min-[400px]:hover:border-gray-300 min-[400px]:dark:hover:border-gray-600 cursor-pointer'
                    : 'border-transparent text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              <Activity className="w-4 h-4" />
              Activity
            </button>
            <button
              onClick={() => account && router.get(`/accounts/${account.id}/workflows`)}
              disabled={!account}
              className={`py-2 min-[400px]:py-4 px-1 border-b-2 min-[400px]:border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'workflows'
                  ? 'border-transparent min-[400px]:border-blue-600 min-[400px]:dark:border-blue-400 text-blue-600 dark:text-blue-400 cursor-pointer'
                  : account
                    ? 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 min-[400px]:hover:border-gray-300 min-[400px]:dark:hover:border-gray-600 cursor-pointer'
                    : 'border-transparent text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              <ListFilter className="w-4 h-4" />
              Workflows
            </button>
            <button
              onClick={() => account && router.get(`/accounts/${account.id}/senders`)}
              disabled={!account}
              className={`py-2 min-[400px]:py-4 px-1 border-b-2 min-[400px]:border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'senders'
                  ? 'border-transparent min-[400px]:border-blue-600 min-[400px]:dark:border-blue-400 text-blue-600 dark:text-blue-400 cursor-pointer'
                  : account
                    ? 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 min-[400px]:hover:border-gray-300 min-[400px]:dark:hover:border-gray-600 cursor-pointer'
                    : 'border-transparent text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              <Users className="w-4 h-4" />
              Senders
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  )
}
